/**
 * Gmail reverse conversation — flips Gmail threads to newest-first
 *
 * Declares a single WAR entry — `js/gmail-reverse-conversation.js`, `img/*.png` and
 * `css/gmail-reverse-conversation.css` — scoped to `https://mail.google.com/*` with no
 * `use_dynamic_url`. The resources are static, but reachable only from Gmail's origin.
 *
 * So this extension IS detectable, but only by mail.google.com. From any other origin
 * the resources are unreachable and a negative result proves nothing — the `matches`
 * pattern scopes them away. That makes this a properly scoped WAR, unlike extensions
 * that hand the same resources to every origin.
 *
 * Manifest verified against version 0.0.16.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kfgepjmmgamniaefbjlbacahkjjnjoaa';

export const gmailReverseConversation: ExtensionDefinition = {
  id: ID,
  name: 'Gmail reverse conversation',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 300_000,
  requiredOrigin: 'https://mail.google.com',
  notes:
    'Static WAR scoped to mail.google.com, so only Gmail can detect it — a properly ' +
    'scoped resource. From any other origin the fetches say nothing.',
  signals: [
    {
      id: 'war-content-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch js/gmail-reverse-conversation.js — the vendor-named content script, ' +
        'static but only exposed to mail.google.com.',
      weight: 1,
      run: () => probeFetch(ID, 'js/gmail-reverse-conversation.js'),
    },
    {
      id: 'war-content-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch css/gmail-reverse-conversation.css — the vendor-named stylesheet, the ' +
        'other origin-scoped resource.',
      weight: 0.5,
      run: () => probeFetch(ID, 'css/gmail-reverse-conversation.css'),
    },
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Fetch img/icon_128.png — a concrete file from the img/*.png glob, corroborating ' +
        'the identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'img/icon_128.png'),
    },
  ],
};
